import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GitCommit, X, Sparkles, Hash, Clock } from 'lucide-react';
import FeatureSpotlight, { useFeatureSpotlight } from './FeatureSpotlight';
import {
  VERSION,
  ERAS,
  formatVersion,
  getBuildNumber,
  getCommit,
  getCommitTime,
} from '../version';

function fmtCommitTime(raw) {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch {
    return raw;
  }
}

function fmtRange(t, era) {
  const m = (s) => (s ? t(`version.months.${s.slice(5, 7)}`) + ' ' + s.slice(0, 4) : '');
  if (!era.to) return `${m(era.from)} → ${t('version.now')}`;
  // same month → single label
  if (era.from.slice(0, 7) === era.to.slice(0, 7)) return m(era.from);
  return `${m(era.from)} → ${m(era.to)}`;
}

// The full version / "about" modal, portalled to body and centered to the
// viewport (per the mandatory modal rule). Shows the current version big, the
// build/commit/compiled metadata, and the retroactive era history.
function VersionModal({ onClose }) {
  const { t } = useTranslation();
  const build = getBuildNumber();
  const commit = getCommit();
  const commitTime = getCommitTime();

  const modal = (
    <div
      onMouseDown={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ margin: 0, maxWidth: 540, width: '100%', maxHeight: '88vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--c-text-muted)', letterSpacing: 0.3 }}>
              AyudaTecno HelpDesk
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <Sparkles size={22} style={{ color: 'var(--c-brand)' }} />
              <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--c-text)' }}>
                v{VERSION.major}.{VERSION.minor}
              </span>
              <span style={{ fontSize: 18, color: 'var(--c-brand)', fontStyle: 'italic' }}>
                "{VERSION.codename}"
              </span>
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </div>

        {/* Build metadata */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '14px 0 18px', fontSize: 12, color: 'var(--c-text-muted)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Hash size={13} /> {t('version.build')} <strong style={{ color: 'var(--c-text)' }}>{build}</strong>
          </span>
          {commit && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }} title={commit}>
              <GitCommit size={13} /> <code style={{ color: 'var(--c-text)' }}>{commit.slice(0, 7)}</code>
            </span>
          )}
          {commitTime && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Clock size={13} /> {fmtCommitTime(commitTime)}
            </span>
          )}
        </div>

        {/* Era history */}
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)', marginBottom: 10 }}>
          {t('version.history')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ERAS.map((era) => (
            <div
              key={era.key}
              style={{
                display: 'flex', gap: 12, padding: 12, borderRadius: 10,
                background: era.current ? 'var(--c-brand-subtle, rgba(59,130,246,0.08))' : 'var(--c-surface-2)',
                border: `1px solid ${era.current ? 'var(--c-brand)' : 'var(--c-border)'}`,
              }}
            >
              <div style={{ minWidth: 64, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: era.current ? 'var(--c-brand)' : 'var(--c-text)' }}>
                  v{era.major}
                </div>
                <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--c-text-muted)' }}>
                  {era.codename}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>
                    {t(`version.eras.${era.key}.title`)}
                  </span>
                  {era.current && (
                    <span className="badge badge-open" style={{ fontSize: 10 }}>{t('version.current')}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-muted)', margin: '2px 0 4px' }}>
                  {fmtRange(t, era)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--c-text-muted)', lineHeight: 1.4 }}>
                  {t(`version.eras.${era.key}.blurb`)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

// The sidebar pill: replaces the old raw git-hash pill with the friendly
// version. Click opens the about/history modal. Hash + build kept in the title
// tooltip for debugging. Self-announces on first visit via FeatureSpotlight.
export default function VersionPill() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const spotlight = useFeatureSpotlight('version_pill_friendly');
  const build = getBuildNumber();
  const commit = getCommit();

  return (
    <>
      <div
        ref={spotlight.anchorRef}
        className="version-pill"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title={`${formatVersion()} · build ${build}${commit ? ` · ${commit.slice(0, 7)}` : ''}`}
        onClick={() => { setOpen(true); spotlight.dismiss(); }}
      >
        <Sparkles size={10} />
        <span>v{VERSION.major}.{VERSION.minor} "{VERSION.codename}"</span>
        <span className="version-pill-sep">&middot;</span>
        <span>build {build}</span>
      </div>

      <FeatureSpotlight
        anchorRef={spotlight.anchorRef}
        show={spotlight.shouldShow}
        onDismiss={spotlight.dismiss}
        title={t('version.spotlight.title')}
        body={t('version.spotlight.body')}
        placement="top"
      />

      {open && <VersionModal onClose={() => setOpen(false)} />}
    </>
  );
}
